import React, { useState, useEffect, useCallback } from "react";
import { logoutUser } from "./config/firebase";
import { saveKey } from "./config/firestore";
import { useAuth } from "./hooks/useAuth";
import { useFirestore } from "./hooks/useFirestore";
import LoginScreen             from "./screens/LoginScreen";
import OnboardingScreen        from "./screens/OnboardingScreen";
import AccueilScreen           from "./screens/AccueilScreen";
import ChecklistScreen         from "./screens/ChecklistScreen";
import AutonomiScreen          from "./screens/AutonomiScreen";
import DepartScreen            from "./screens/DepartScreen";
import DossiersScreen          from "./screens/DossiersScreen";
import DepensesScreen          from "./screens/DepensesScreen";
import DiagnosticScreen        from "./screens/DiagnosticScreen";
import RapportScreen           from "./screens/RapportScreen";
import SecoursScreen           from "./screens/SecoursScreen";
import PremiumScreen           from "./screens/PremiumScreen";
import CertificatPublicScreen  from "./screens/CertificatPublicScreen";
import OutilsScreen            from "./screens/OutilsScreen";
import TutoScreen              from "./components/TutoScreen";
import LandingPage             from "./components/LandingPage";
import { translations }        from "./config/translations";
import "./App.css";

const C = {
  bg:         "#F8F6F1",
  forest:     "#2D5A3D",
  darkForest: "#1C3A28",
  terracotta: "#C4622D",
  blue:       "#5B8DB8",
  text:       "#1C1C1E",
  muted:      "#6B7280",
};

function compressImage(dataUrl, maxPx = 800, quality = 0.5) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width  * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.src = dataUrl;
  });
}

function SplashScreen({ fading }) {
  const barRef = React.useRef(null);
  React.useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (barRef.current) barRef.current.style.width = "100%";
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0,
      background: `linear-gradient(160deg, ${C.darkForest}, ${C.forest})`,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      zIndex: 9999,
      opacity: fading ? 0 : 1,
      transition: "opacity 0.5s ease",
      fontFamily: "-apple-system, 'SF Pro Display', sans-serif",
    }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{ fontSize: 80, marginBottom: 18 }}>🚐</div>
        <div style={{
          fontSize: 32, fontWeight: 900, color: "white",
          letterSpacing: 4, textShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}>CHECKVAN</div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginTop: 8, letterSpacing: 0.5 }}>
          Le carnet de bord de votre van
        </div>
      </div>
      <div style={{ width: "55%", height: 5, background: "rgba(255,255,255,0.2)", borderRadius: 99, overflow: "hidden" }}>
        <div ref={barRef} style={{
          height: "100%", width: "0%",
          background: "white", borderRadius: 99,
          transition: "width 2s linear",
        }} />
      </div>
    </div>
  );
}

export default function App() {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.uid ?? null;
  const { load, save, syncStatus } = useFirestore(userId);

  const [van,           setVan]           = useState(null);
  const [checks,        setChecks]        = useState({});
  const [autonomie,     setAutonomie]     = useState({ eau: 70, gaz: 60, batterie: 80, eauxUsees: 20 });
  const [depenses,      setDepenses]      = useState([]);
  const [dossiers,      setDossiers]      = useState({});
  const [voyage,        setVoyage]        = useState({ depart: { date: "", km: "" }, arrivee: { date: "", km: "" } });
  const [persoItems,    setPersoItems]    = useState([]);
  const [lastCheckDate, setLastCheckDate] = useState(null);
  const [tab,           setTabState]      = useState("accueil");
  const [checklistSub,  setChecklistSub]  = useState("vehicule");
  const [showSplash,    setShowSplash]    = useState(true);
  const [splashFading,  setSplashFading]  = useState(false);
  const [showMenu,      setShowMenu]      = useState(false);
  const [showSOS,       setShowSOS]       = useState(false);
  const [showPremium,   setShowPremium]   = useState(false);
  const [showTuto,      setShowTuto]      = useState(false);
  const [showLogin,     setShowLogin]     = useState(false);
  const [lang,          setLang]          = useState(() => localStorage.getItem("checkvan_lang") || "fr");
  const t = translations[lang] || translations["fr"];

  const CHECKLIST_SUBTABS = [
    { id: "vehicule",  label: t.vehicule  || "Véhicule"  },
    { id: "depart",    label: t.depart    || "Départ"    },
    { id: "autonomie", label: t.autonomie || "Autonomie" },
  ];

  const contentRef    = React.useRef(null);
  const dataLoadedRef = React.useRef(false);

  // Splash
  useEffect(() => {
    const t1 = setTimeout(() => setSplashFading(true), 2000);
    const t2 = setTimeout(() => setShowSplash(false), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Premier lancement
  useEffect(() => {
    if (!localStorage.getItem("checkvan_tuto_seen")) {
      setShowTuto(true);
    }
  }, []);

  // Scroll en haut au changement d'onglet
  const setTab = useCallback((newTab) => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
    setTabState(newTab);
  }, []);

  // Chargement données Firebase
  useEffect(() => {
    if (!userId) { dataLoadedRef.current = false; return; }
    (async () => {
      try {
        const data = await load();
        if (!data) { dataLoadedRef.current = true; return; }
        if (data.checks)    setChecks(typeof data.checks === "string" ? JSON.parse(data.checks) : data.checks);
        if (data.autonomie) setAutonomie(typeof data.autonomie === "string" ? JSON.parse(data.autonomie) : data.autonomie);
        if (data.depenses)  setDepenses(typeof data.depenses === "string" ? JSON.parse(data.depenses) : data.depenses);
        if (data.dossiers)  setDossiers(typeof data.dossiers === "string" ? JSON.parse(data.dossiers) : data.dossiers);
        if (data.voyage)    setVoyage(typeof data.voyage === "string" ? JSON.parse(data.voyage) : data.voyage);
        if (data.persoItems) setPersoItems(typeof data.persoItems === "string" ? JSON.parse(data.persoItems) : data.persoItems);
        if (data.van) {
          const v = typeof data.van === "string" ? JSON.parse(data.van) : data.van;
          const photo = data.van_photo || "";
          const vanData = { ...v, ...(photo ? { photo } : {}) };
          dataLoadedRef.current = true;
          setVan(vanData);
        } else {
          dataLoadedRef.current = true;
        }
      } catch (e) {
        console.error("[Firestore load] erreur:", e);
      }
    })();
  }, [userId, load]);

  // Sauvegarde auto — bloquée tant que le chargement initial n'est pas terminé
  // (évite d'écraser Firestore avec les valeurs vides de l'état initial)
  useEffect(() => {
    if (!userId || !van || !dataLoadedRef.current) return;
    const { photo, ...vanNoPhoto } = van;
    save("van", vanNoPhoto);
  }, [van, userId, save]);

  useEffect(() => { if (!userId || !dataLoadedRef.current) return; save("checks",    checks);    }, [checks,    userId, save]);
  useEffect(() => { if (!userId || !dataLoadedRef.current) return; save("autonomie", autonomie); }, [autonomie, userId, save]);
  useEffect(() => { if (!userId || !dataLoadedRef.current) return; save("depenses",  depenses);  }, [depenses,  userId, save]);
  useEffect(() => { if (!userId || !dataLoadedRef.current) return; save("dossiers",  dossiers);  }, [dossiers,  userId, save]);
  useEffect(() => { if (!userId || !dataLoadedRef.current) return; save("voyage",    voyage);    }, [voyage,    userId, save]);
  useEffect(() => { if (!userId || !dataLoadedRef.current) return; save("persoItems",persoItems);}, [persoItems,userId, save]);

  // Reset au logout
  useEffect(() => {
    if (!userId) {
      dataLoadedRef.current = false;
      setVan(null);
      setChecks({});
      setDepenses([]);
      setDossiers({});
      setVoyage({ depart: { date: "", km: "" }, arrivee: { date: "", km: "" } });
      setPersoItems([]);
    }
  }, [userId]);

  // Calcul score : true (entretien/sécurité) ou "ok" (départ) = validé
  const checkValues = Object.values(checks).filter(v => v !== null && v !== undefined);
  const checkDone   = checkValues.filter(v => v === true || v === "ok").length;
  const checkTotal  = checkValues.length;
  const score = checkTotal ? Math.round((checkDone / checkTotal) * 100) : 0;

  const handlePhotoChange = useCallback(async (dataUrl) => {
    const compressed = await compressImage(dataUrl, 800, 0.5);
    setVan(prev => ({ ...(prev || {}), photo: compressed }));
    if (userId) {
      await saveKey(userId, "van_photo", compressed);
    }
  }, [userId]);

  const handlePhotoDelete = useCallback(() => {
    setVan(prev => { const { photo, ...rest } = prev || {}; return rest; });
    if (userId) save("van_photo", "");
  }, [userId, save]);

  const handleVanUpdate = useCallback((updates) => {
    setVan(prev => ({ ...(prev || {}), ...updates }));
  }, []);

  const handleCheck = useCallback((itemId, val) => {
    setChecks(prev => ({ ...prev, [itemId]: val }));
    setLastCheckDate(new Date().toISOString());
  }, []);

  const handleVanCreated = useCallback(async (newVan) => {
    if (userId) {
      await saveKey(userId, "van", newVan);
    }
    dataLoadedRef.current = true;
    setVan(newVan);
    setTab("accueil");
  }, [setTab, userId]);

  const handleAutonomieChange = useCallback((newAuto) => setAutonomie(newAuto), []);

  // QR code public certificate page — no auth required
  const certMatch = window.location.pathname.match(/^\/certificat\/(.+)$/);
  if (certMatch) return <CertificatPublicScreen certId={certMatch[1]} />;

  if (showSplash) return <SplashScreen fading={splashFading} />;

  if (authLoading) {
    return (
      <div style={{
        maxWidth: 430, margin: "0 auto", minHeight: "100vh",
        background: C.bg, display: "flex", alignItems: "center",
        justifyContent: "center", color: C.muted, fontSize: 14,
        fontFamily: "-apple-system, 'SF Pro Display', sans-serif",
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🚐</div>
          <div>Chargement...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    if (showLogin) return <LoginScreen lang={lang} setLang={setLang} />;
    return <LandingPage onGetStarted={() => setShowLogin(true)} lang={lang} />;
  }
  if (!van)  return <OnboardingScreen onCreated={handleVanCreated} t={t} />;

  const TABS = [
    { id: "accueil",   label: t.accueil   || "Accueil",   icon: "🏠" },
    { id: "checklist", label: t.checklist || "Checklist", icon: "✅" },
    { id: "dossiers",    label: t.dossiers    || "Dossiers",    icon: "📁" },
    { id: "depenses",    label: t.depenses    || "Dépenses",    icon: "💰" },
    { id: "diagnostic",  label: t.diagnostic  || "Diagnostic",  icon: "🔧" },
    { id: "outils",      label: t.outils      || "Outils",      icon: "🧰" },
    { id: "rapport",     label: t.rapport     || "Rapport",     icon: "📊" },
  ];

  const tabLabels = {
    accueil:   t.accueil   || "Accueil",
    checklist: t.checklist || "Checklist",
    autonomie: t.autonomie || "Autonomie",
    dossiers:  t.dossiers  || "Dossiers",
    depenses:  t.depenses  || "Dépenses",
    rapport:   t.rapport   || "Rapport",
  };

  return (
    <div style={{
      maxWidth: 430, margin: "0 auto", minHeight: "100vh",
      background: C.bg, color: C.text,
      fontFamily: "-apple-system, 'SF Pro Display', 'Segoe UI', sans-serif",
      position: "relative", paddingBottom: 80,
    }}>
      {showTuto && <TutoScreen onClose={() => { localStorage.setItem("checkvan_tuto_seen", "1"); setShowTuto(false); }} lang={lang} />}

      {/* Menu overlay */}
      {showMenu && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }}
          onClick={() => setShowMenu(false)}
        >
          <div
            style={{
              position: "absolute", top: 0, right: 0, width: 280,
              height: "100%", background: C.bg,
              boxShadow: "-4px 0 24px rgba(0,0,0,0.2)",
              padding: "60px 24px 32px",
              display: "flex", flexDirection: "column",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 22, fontWeight: 900, color: C.darkForest, marginBottom: 4 }}>
              {van?.nom || (t.monVan || "Mon Van")}
            </div>
            {van?.immat && (
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 24, letterSpacing: 1 }}>
                {van.immat}
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, marginBottom: 6 }}>{t.compte || "COMPTE"}</div>
              <div style={{
                background: "white", borderRadius: 12, padding: "12px 14px",
                fontSize: 13, color: C.text, marginBottom: 20,
                border: "1px solid rgba(44,90,61,0.1)",
              }}>
                {user?.email}
              </div>
              <div style={{ fontSize: 11, color: C.muted, textAlign: "center", marginBottom: 16 }}>
                {syncStatus}
              </div>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setVan(null);
                }}
                style={{
                  width: "100%", padding: "12px 0", borderRadius: 12, border: "none",
                  background: `${C.terracotta}15`, color: C.terracotta,
                  fontWeight: 700, cursor: "pointer", fontSize: 14, marginBottom: 10,
                }}
              >
                {t.reconfigurerVan || "🔄 Reconfigurer le van"}
              </button>
            </div>
            <button
              onClick={() => { logoutUser(); setShowMenu(false); }}
              style={{
                width: "100%", padding: "13px 0", borderRadius: 12, border: "none",
                background: "#FEF2F2", color: "#DC2626",
                fontWeight: 800, cursor: "pointer", fontSize: 14,
              }}
            >
              {t.seDeconnecter || "⏏ Se déconnecter"}
            </button>
          </div>
        </div>
      )}

      {/* Header sticky */}
      <div style={{
        padding: "14px 16px 12px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        position: "sticky", top: 0, zIndex: 50,
        background: C.bg,
        borderBottom: "1px solid rgba(44,90,61,0.08)",
        boxShadow: "0 1px 8px rgba(44,90,61,0.06)",
      }}>
        <div style={{ width: 44 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>🚐</span>
          <h1 style={{ color: C.darkForest, fontSize: 18, fontWeight: 900, margin: 0, letterSpacing: 0.5 }}>
            CheckVan
          </h1>
        </div>
        <button
          onClick={() => setShowMenu(true)}
          style={{
            background: "white", border: `1.5px solid rgba(44,90,61,0.15)`,
            borderRadius: 10, padding: "6px 11px", cursor: "pointer",
            fontSize: 14, color: C.forest, fontWeight: 700,
            boxShadow: "0 1px 4px rgba(44,90,61,0.1)",
          }}
        >
          ☰
        </button>
      </div>

      {/* Contenu */}
      <div ref={contentRef} style={{ overflowY: "auto", maxHeight: "calc(100vh - 118px)" }}>
        {tab === "accueil" && (
          <AccueilScreen
            van={van}
            score={score}
            setTab={setTab}
            onPhotoChange={handlePhotoChange}
            onPhotoDelete={handlePhotoDelete}
            onVanUpdate={handleVanUpdate}
            dossiers={dossiers}
            lastCheckDate={lastCheckDate}
            autonomie={autonomie}
            checks={checks}
            onSOS={() => setShowSOS(true)}
            userEmail={user?.email}
            onShowPremium={() => setShowPremium(true)}
            depenses={depenses}
            t={t}
            lang={lang}
            setLang={setLang}
          />
        )}
        {tab === "checklist" && (
          <div>
            {checklistSub === "vehicule" && (
              <ChecklistScreen
                checks={checks}
                onCheck={handleCheck}
                t={t}
                subTabBar={
                  <div style={{ display: "flex", gap: 0, padding: "0 16px 12px", background: "#2D5A3D" }}>
                    {CHECKLIST_SUBTABS.map(st => (
                      <button
                        key={st.id}
                        onClick={() => setChecklistSub(st.id)}
                        style={{
                          flex: 1, padding: "8px 0", border: "none", cursor: "pointer",
                          borderRadius: 9, fontFamily: "inherit",
                          fontSize: 13, fontWeight: 700,
                          background: checklistSub === st.id ? "#1C3A28" : "#C4622D",
                          color: "white",
                          transition: "all 0.2s",
                        }}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                }
              />
            )}
            {checklistSub === "depart" && (
              <DepartScreen
                checks={checks}
                onCheck={handleCheck}
                persoItems={persoItems}
                setPersoItems={setPersoItems}
                t={t}
                subTabBar={
                  <div style={{ display: "flex", gap: 0, padding: "0 16px 12px", background: "#2D5A3D" }}>
                    {CHECKLIST_SUBTABS.map(st => (
                      <button
                        key={st.id}
                        onClick={() => setChecklistSub(st.id)}
                        style={{
                          flex: 1, padding: "8px 0", border: "none", cursor: "pointer",
                          borderRadius: 9, fontFamily: "inherit",
                          fontSize: 13, fontWeight: 700,
                          background: checklistSub === st.id ? "#1C3A28" : "#C4622D",
                          color: "white",
                          transition: "all 0.2s",
                        }}
                      >
                        {st.label}
                      </button>
                    ))}
                  </div>
                }
              />
            )}
            {checklistSub === "autonomie" && (
              <div>
                <div style={{ display: "flex", gap: 0, padding: "0 16px 12px", background: "#2D5A3D" }}>
                  {CHECKLIST_SUBTABS.map(st => (
                    <button
                      key={st.id}
                      onClick={() => setChecklistSub(st.id)}
                      style={{
                        flex: 1, padding: "8px 0", border: "none", cursor: "pointer",
                        borderRadius: 9, fontFamily: "inherit",
                        fontSize: 13, fontWeight: 700,
                        background: checklistSub === st.id ? "#1C3A28" : "#C4622D",
                        color: "white",
                        transition: "all 0.2s",
                      }}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>
                <AutonomiScreen autonomie={autonomie} setAutonomie={handleAutonomieChange} t={t} />
              </div>
            )}
          </div>
        )}
        {tab === "dossiers" && (
          <DossiersScreen dossiers={dossiers} setDossiers={setDossiers} depenses={depenses} userId={userId} t={t} />
        )}
        {tab === "depenses" && (
          <DepensesScreen depenses={depenses} setDepenses={setDepenses} voyage={voyage} onVoyageChange={setVoyage} userId={userId} t={t} />
        )}
        {tab === "diagnostic" && (
          <DiagnosticScreen t={t} />
        )}
        {tab === "outils" && (
          <OutilsScreen t={t} />
        )}
        {tab === "rapport" && (
          <RapportScreen
            van={van}
            depenses={depenses}
            checks={checks}
            userId={userId}
            voyage={voyage}
            t={t}
          />
        )}
      </div>

      {/* Écran SOS */}
      {showSOS && (
        <SecoursScreen onClose={() => setShowSOS(false)} dossiers={dossiers} t={t} />
      )}

      {/* Écran Premium */}
      {showPremium && (
        <PremiumScreen onClose={() => setShowPremium(false)} t={t} />
      )}

      {/* Tab bar — 6 onglets */}
      <nav style={{
        position: "fixed", bottom: 0,
        left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 430,
        background: "white",
        borderTop: "1px solid rgba(44,90,61,0.1)",
        display: "flex", justifyContent: "space-around",
        padding: "6px 0 14px", zIndex: 100,
        boxShadow: "0 -4px 20px rgba(44,90,61,0.08)",
      }}>
        {TABS.map(tb => {
          const on = tab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                cursor: "pointer", flex: 1, border: "none", background: "transparent", padding: "3px 0",
              }}
            >
              <div style={{
                width: 38, height: 38, borderRadius: 11,
                background: on ? C.forest : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18,
                boxShadow: on ? `0 3px 10px ${C.forest}55` : "none",
                transition: "all 0.2s",
              }}>
                {tb.icon}
              </div>
              <span style={{
                fontSize: 8, fontWeight: 700, letterSpacing: 0.3,
                color: on ? C.forest : C.muted,
              }}>
                {tb.label.toUpperCase()}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
